import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Sparkles, BookOpen, PlayCircle } from 'lucide-react';
import { classifyTheme } from '../../lib/themeClassifier';
import { THEME_MICRO_SUMMARIES } from '../../lib/themeKeywords';

export default function ContinueThemes({ swirls, items = [] }) {
  const navigate = useNavigate();

  // Generate theme cards from items if no swirls exist
  const themeCards = useMemo(() => {
    if (swirls.length > 0) {
      return swirls.slice(0, 3).map(swirl => ({
        id: swirl.id,
        title: swirl.title,
        topics: swirl.topics || [],
        preview: swirl.preview || [],
        summary: swirl.summary,
        description: swirl.description
      }));
    }

    // Extract top themes from items
    if (items.length === 0) {
      return [];
    }

    const themeCounts = {};
    const themeItems = {};

    items.forEach(item => {
      // Extract themes from various sources
      const themes = [
        ...(item.extractedThemes || []),
        ...(item.auto_tags?.topics || []),
        ...(item.meta?.ai_analysis?.topics || []),
        ...(item.primary_tags || []),
        ...(item.microTopics || [])
      ].filter(Boolean);

      // Classify each theme
      themes.forEach(topic => {
        if (topic && typeof topic === 'string') {
          const theme = classifyTheme([topic.toLowerCase()]);
          if (theme && theme !== 'General') {
            themeCounts[theme] = (themeCounts[theme] || 0) + 1;
            if (!themeItems[theme]) {
              themeItems[theme] = [];
            }
            themeItems[theme].push(item);
          }
        }
      });
    });

    // Get top 3 themes
    const topThemes = Object.entries(themeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([theme, count]) => ({
        id: `theme-${theme}`,
        title: theme,
        count,
        items: themeItems[theme] || []
      }));

    return topThemes;
  }, [swirls, items]);

  if (themeCards.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-[22px] p-6" style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.06)', overflow: 'visible' }}>
      <h3 className="text-base font-semibold mb-5 tracking-tight" style={{ color: '#111111' }}>Continue With These Themes</h3>
      <div className="space-y-2" style={{ overflow: 'visible', position: 'relative' }}>
        {themeCards.map((card, i) => {
          const icons = [Sparkles, BookOpen, PlayCircle];
          const Icon = icons[i] || Sparkles;
        
          const KNOWN_THEMES = ['Technology', 'Business', 'Creativity', 'HealthFitness', 'Psychology', 'Productivity', 'WealthCareer', 'Lifestyle'];
          
          // Handle both swirl objects and theme objects
          const isSwirl = 'preview' in card || 'topics' in card;
          const title = card.title || `Theme ${i + 1}`;
          
          const titleAsTheme = KNOWN_THEMES.find(t => 
            t.toLowerCase() === title?.toLowerCase() ||
            title?.toLowerCase().includes(t.toLowerCase())
          );
          
          const allTopics = isSwirl ? [
            ...(card.topics || []),
            ...(card.preview?.flatMap(item => item.auto_tags?.topics || []) || []),
            ...(card.preview?.flatMap(item => item.meta?.ai_analysis?.topics || []) || []),
            ...(card.preview?.flatMap(item => item.primary_tags || []) || []),
          ] : [
            ...(card.items?.flatMap(item => item.extractedThemes || []) || []),
            ...(card.items?.flatMap(item => item.microTopics || []) || []),
            ...(card.items?.flatMap(item => item.auto_tags?.topics || []) || []),
          ];
          
          const primaryTheme = titleAsTheme || classifyTheme(allTopics) || title || "General";
          
          const subtitle = 
            THEME_MICRO_SUMMARIES[primaryTheme] ||
            (isSwirl ? (card.topics?.[0] || card.summary || card.description) : null) ||
            (card.count ? `${card.count} items` : null) ||
            "Insights curated around your interests.";
          
          return (
            <div
              key={card.id || i} 
              onClick={() => {
                if (primaryTheme && primaryTheme !== "General") {
                  const url = `/swirlspace?theme=${encodeURIComponent(primaryTheme)}`;
                  navigate(url);
                } else if (card.id && card.id.startsWith('swirl-')) {
                  const url = `/swirlspace?swirl=${card.id.replace('swirl-', '')}`;
                  navigate(url);
                } else {
                  navigate(`/swirlspace`);
                }
              }}
              className="group relative flex items-center gap-3 p-3 rounded-[20px] cursor-pointer transition-all duration-200 bg-white"
              style={{ 
                boxShadow: '0 2px 6px rgba(0,0,0,0.06)', 
                position: 'relative', 
                zIndex: 1,
                overflow: 'visible'
              }}
            >
              <div 
                className="absolute left-0 top-0 bottom-0 w-1 bg-[#7C3AED] opacity-0 group-hover:opacity-100 transition-opacity rounded-l-[20px]" 
                style={{ 
                  zIndex: 10,
                  left: '-1px'
                }}
              ></div>
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'rgba(136, 92, 255, 0.06)' }}
              >
                <Icon size={18} style={{ color: '#7C3AED' }} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-[13px] font-semibold" style={{ color: '#111111' }}>
                  {title}
                </h4>
                <p className="text-[12px] line-clamp-1 mt-1" style={{ color: '#555555' }}>
                  {subtitle}
                </p>
              </div>
              <ChevronRight size={14} style={{ color: '#999999' }} className="group-hover:text-[#7C3AED] shrink-0 transition-colors" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
